import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import useDialogAlert from '@/hooks/useDialogAlert';
import useFocusOnInput from '@/hooks/useFocusOnInput';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { urlValidationSchema } from '@/util/validation/schemas/dialog-schemas';

import ToolkitWrapper from '../../ToolkitWrapper';

type UrlActionForm = { url: string };

const ToolkitActionUrl = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { type, actionId, url } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as Extract<Action, { type: 'url' }>;
    return {
      type: selectedAction.type,
      actionId: selectedAction.action_id,
      url: selectedAction.url,
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  const form = useForm<UrlActionForm>({
    mode: 'onChange',
    defaultValues: { url },
    resolver: yupResolver(urlValidationSchema) as Resolver<UrlActionForm>,
  });
  const {
    control,
    reset,
    trigger,
    setFocus,
    formState: { errors },
  } = form;

  // Reset form when switching to a different action
  useEffect(() => {
    reset({ url });
  }, [actionId, reset]);

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.url'),
    type,
  });

  useEffect(() => {
    setFieldError('url', errors.url?.message);
  }, [errors.url?.message, setFieldError]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  useFocusOnInput('url', errors, setFocus);

  return (
    <ToolkitWrapper type="url">
      <Form {...form}>
        <div className="pb-6">
          <FormField
            control={control}
            name="url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('field.url')}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    minRows={1}
                    maxRows={4}
                    className="resize-none break-all"
                    placeholder={t('dialog.url_placeholder')}
                    onChange={(event) => {
                      field.onChange(event);
                      dispatch(updateAction({ actionId, action: { url: event.target.value } }));
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>
    </ToolkitWrapper>
  );
};

export default ToolkitActionUrl;
